import {
  Body,
  Col,
  Container,
  Flex,
  Shape,
  SubTitle,
  styled,
} from '@teko/ds-desktop';
import { ChevronDownOutlined, ChevronUpOutlined } from '@teko/ds-icon';
import { FAQ } from '@teko/types/ecommerce';
import { useTenantConfig } from 'contexts';
import { t } from 'i18n';
import Head from 'next/head';
import { memo, useRef, useState } from 'react';

export const FAQSection = ({
  faq,
  style,
  borderRadius = 'm',
}: {
  faq: Array<FAQ>;
  style?: React.CSSProperties;
  // SGC new layout uses 'xl' (16px); other pages keep the default 'm' (8px).
  borderRadius?: 's' | 'm' | 'l' | 'xl';
}) => {
  const { isMobile } = useTenantConfig();
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faq.map(({ question, answer }) => ({
                '@type': 'Question',
                name: question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: answer,
                },
              })),
            }),
          }}
        />
      </Head>
      <Container
        style={style ? style : isMobile ? { padding: '0px' } : {}}
        marginBottom="s8"
        maxWidth="100%"
      >
        <Shape
          borderRadius={borderRadius}
          backgroundColor="white"
          style={{ overflow: 'hidden' }}
        >
          <Shape padding="s16" borderBottom="1px solid #0000000F">
            <StyledTitle>{t('Câu hỏi thường gặp')}</StyledTitle>
          </Shape>
          {faq.map((item, index) => (
            <MemoFAQItem
              key={index}
              item={item}
              isLastItem={index === faq.length - 1}
            />
          ))}
        </Shape>
      </Container>
    </>
  );
};

interface IFAQItemProps {
  item: FAQ;
  isLastItem: boolean;
}

const FAQItem = ({ item, isLastItem }: IFAQItemProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isShowingAnswer, setShowingAnswer] = useState(false);
  const answers = item.answer?.split('\n');

  return (
    <Shape
      borderBottom={isLastItem ? '' : '1px solid #0000000F'}
      padding={['s16', 's24']}
    >
      <Flex
        direction="row"
        justify="space-between"
        align="center"
        backgroundColor="white"
        gap="s4"
        onClick={() => {
          setShowingAnswer((previous) => !previous);
        }}
        style={{ cursor: 'pointer' }}
      >
        <SubTitle level={2} color="textTitle">
          {item.question}
        </SubTitle>
        <Col width={15}>
          {isShowingAnswer ? (
            <ChevronUpOutlined size={14} color="#43464C" />
          ) : (
            <ChevronDownOutlined size={14} color="#43464C" />
          )}
        </Col>
      </Flex>
      <Shape
        backgroundColor="white"
        style={{
          overflow: 'hidden',
          transition: 'height 0.3s ease',
          height: isShowingAnswer ? contentRef.current?.clientHeight : 0,
        }}
      >
        <Shape paddingTop="s12" ref={contentRef}>
          {answers?.map((line, index) => (
            <Body
              level={1}
              color="textTitle"
              key={index}
              dangerouslySetInnerHTML={{ __html: line }}
            />
          ))}
        </Shape>
      </Shape>
    </Shape>
  );
};

const MemoFAQItem = memo(
  FAQItem,
  (prevProps: IFAQItemProps, nextProps: IFAQItemProps) => {
    return (
      prevProps.item === nextProps.item &&
      prevProps.isLastItem === nextProps.isLastItem
    );
  },
);

const StyledTitle = styled.h3`
  line-height: 28px;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 0;
`;
